<template>
  <div id="fusion_body_container" ref="fusion_body_container" :class="{'px-6 pt-6 pb-3':page_type === 'Spark' && show_splash}">
    <template v-if="has_access">
      <!-- On the Rey chat the wrapper carries a faint rule along its bottom edge, so the conversation
           reads as scrolling beneath the navbar. The rule is on this wrapper rather than the navbar
           because the wrapper runs edge to edge and the navbar sits inside its px-2. pb-1 is dropped
           there so the Rey Assistant underline lands on the rule instead of floating 4px above it.
           Not on the splash, which has no conversation to sit beneath anything. -->
      <div class="px-2 text-text-default" :class="{'pb-1':page_type !== 'ContactDetails' && !show_spark_navbar_rule, 'border-b border-border-separator-subtle':show_spark_navbar_rule}">
        <!-- :key on page_type forces a clean re-mount whenever page_type changes value.
             This guards against a Vue prop-binding race seen in ERA's embedded webview where
             the navbar mounts before page_type is settled, leaving all v-if branches false
             and producing a blank 60px gap that doesn't refresh on the eventual reactive
             update. Re-mounting fixes the stale-prop class of issue without touching Navbar
             internals. -->
        <navbar v-if="page_type" :key="page_type" :display-type="page_type" :spark-splash-active="page_type === 'Spark' && show_splash" :show-options-trigger="!hideOptionsMenu" @openOptionsMenu="show_options = Date.now()" />
      </div>
      <!-- "Chat with Rey" pill: hidden via `false &&` so the chat window claims the full height
           (chrome_height is measured from .fusion_body's top, so dropping this container reclaims
           its space automatically). Kept intact — along with the Recommendations pill below it —
           so it can be flipped back on later. -->
      <div v-if="false && page_type === 'Spark' && !show_splash" class="self-stretch px-4 pt-5 pb-2 inline-flex flex-col justify-start items-start gap-2.5 w-full overflow-x-auto">
        <div class="inline-flex justify-between items-center gap-2">
          <div class="relative" :class="[nav_item_class, active_tab === 'Chat' ? nav_item_active_class : nav_item_inactive_class]"
               @click.prevent="setActiveTab('Chat')">
            <img :src="rey_chat_icon" :alt="$t('common.chatWithRey')" class="w-4 h-4" />
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.chatWithRey') }}</div>
            </div>
          </div>
          <div v-if="false" class="relative" :class="[nav_item_class, active_tab === 'Recommendations' ? nav_item_active_class : nav_item_inactive_class]"
               @click.prevent="setActiveTab('Recommendations')">
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.recommendations') }}</div>
              <span class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">9</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="(has_chat_access || has_tac_access) && page_type === 'InternalMessaging' && !internal_messaging_state" class="self-stretch px-4 pt-5 pb-2 inline-flex flex-col justify-start items-start gap-2.5 w-full overflow-x-auto">
        <div class="inline-flex justify-between items-center gap-2">
          <!-- All pill: hidden via `false &&` so we can flip it back on later. Keep the rest of
               the wiring intact (all_threads_display computed, 'All' switch branches in
               MessengerWindow). -->
          <div v-if="has_chat_access" class="relative" :class="[nav_item_class, active_tab === 'All' ? nav_item_active_class : nav_item_inactive_class]" @click.prevent="setActiveTab('All')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" :class="active_tab === 'All' ? 'text-brand' : 'text-icon-default'">
              <path d="M6.36732 7.66634C5.53889 7.66634 4.86732 6.99474 4.86732 6.16634C4.86732 5.33791 5.53889 4.66634 6.36732 4.66634C7.19572 4.66634 7.86732 5.33791 7.86732 6.16634C7.86732 6.99474 7.19572 7.66634 6.36732 7.66634ZM6.66732 13.165V10.933C6.66732 10.6074 6.76345 10.3081 6.93672 10.0413C6.73978 10.0139 6.53852 9.99967 6.33398 9.99967C5.28866 9.99967 4.32978 10.3698 3.58126 10.9861C4.30004 12.0477 5.39127 12.8365 6.66732 13.165ZM2.96922 9.77301C3.9102 9.07754 5.07407 8.66634 6.33398 8.66634C7.02965 8.66634 7.69605 8.79167 8.31178 9.02107C8.89712 8.79221 9.59558 8.66634 10.334 8.66634C11.4405 8.66634 12.4572 8.94894 13.138 9.43721C13.2657 8.97987 13.334 8.49774 13.334 7.99967C13.334 5.05415 10.9462 2.66634 8.00065 2.66634C5.05513 2.66634 2.66732 5.05415 2.66732 7.99967C2.66732 8.62134 2.77369 9.21821 2.96922 9.77301ZM12.5869 10.7236C12.3248 10.3681 11.4479 9.99967 10.334 9.99967C8.99658 9.99967 8.00065 10.5308 8.00065 10.933V13.333C9.95098 13.333 11.6569 12.2861 12.5869 10.7236ZM8.00065 14.6663C4.31875 14.6663 1.33398 11.6815 1.33398 7.99967C1.33398 4.31777 4.31875 1.33301 8.00065 1.33301C11.6825 1.33301 14.6673 4.31777 14.6673 7.99967C14.6673 11.6815 11.6825 14.6663 8.00065 14.6663ZM10.334 8.33301C9.59758 8.33301 9.00065 7.73607 9.00065 6.99967C9.00065 6.26329 9.59758 5.66634 10.334 5.66634C11.0704 5.66634 11.6673 6.26329 11.6673 6.99967C11.6673 7.73607 11.0704 8.33301 10.334 8.33301Z" fill="currentColor"/>
            </svg>
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.all') }}</div>
              <span v-if="all_unread_count>0" class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">{{all_unread_count }}</span>
            </div>
          </div>
          <div v-if="has_chat_access" class="relative" :class="[nav_item_class, active_tab === 'Messages' ? nav_item_active_class : nav_item_inactive_class]" @click.prevent="setActiveTab('Messages')">
            <i class="ri-chat-3-line leading-none" :class="active_tab === 'Messages' ? 'text-brand' : 'text-icon-default'" />
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.dms') }}</div>
              <span v-if="direct_messages_unread_count>0" class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">{{direct_messages_unread_count }}</span>
            </div>
          </div>
          <div v-if="has_chat_access && is_autovision_platform" class="relative" :class="[nav_item_class, active_tab === 'Threads' ? nav_item_active_class : nav_item_inactive_class]" @click.prevent="setActiveTab('Threads')">
            <i class="ri-hashtag leading-none" :class="active_tab === 'Threads' ? 'text-brand' : 'text-icon-default'" />
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.threads') }}</div>
              <span v-if="recent_messages_unread_count>0" class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">{{recent_messages_unread_count }}</span>
            </div>
          </div>
          <div v-if="has_chat_access" class="relative" :class="[nav_item_class, active_tab === 'Channels' ? nav_item_active_class : nav_item_inactive_class]" @click.prevent="setActiveTab('Channels')">
            <i class="ri-team-line leading-none" :class="active_tab === 'Channels' ? 'text-brand' : 'text-icon-default'" />
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.channels') }}</div>
              <span v-if="channels_unread_count>0" class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">{{channels_unread_count }}</span>
            </div>
          </div>
          <div v-if="has_tac_access" class="relative" :class="[nav_item_class, active_tab === 'Support' ? nav_item_active_class : nav_item_inactive_class]" @click.prevent="setActiveTab('Support')">
            <i class="ri-customer-service-2-fill leading-none" :class="active_tab === 'Support' ? 'text-brand' : 'text-icon-default'" />
            <div class="flex justify-start items-center gap-2">
              <div :class="nav_item_label_class">{{ $t('common.support') }}</div>
              <span v-if="support_messages_unread_count>0" class="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-brand-subtle/30 text-[11px] font-semibold leading-none text-brand">{{support_messages_unread_count }}</span>
            </div>
          </div>
          <div v-if="false" class="h-8 p-2 rounded-md outline outline-1 outline-offset-[-1px] outline-border-separator flex justify-center items-center gap-1">
            <div class="flex justify-start items-center gap-1.5">
              <div data-mode="Light" data-name="chip-mentions" data-size="Small (Default)" data-type="line" class="w-4 h-4 relative">
                <div class="w-4 h-4 left-0 top-0 absolute overflow-hidden">
                  <div class="w-3.5 h-3.5 left-[1px] top-[1px] absolute bg-surface-3"></div>
                </div>
              </div>
              <div class="flex justify-start items-center gap-1">
                <div class="justify-center text-text-default/80 text-xs font-medium leading-4 tracking-tight">{{ $t('common.mentions') }}</div>
              </div>
            </div>
          </div>
          <div v-if="false" class="h-8 p-2 rounded-md outline outline-1 outline-offset-[-1px] outline-border-separator flex justify-center items-center gap-1">
            <div class="flex justify-start items-center gap-1.5">
              <div data-mode="Light" data-name="chip-drafts" data-size="Small (Default)" data-type="line" class="w-4 h-4 relative">
                <div class="w-4 h-4 left-0 top-0 absolute overflow-hidden">
                  <div class="w-3.5 h-3.5 left-[1px] top-[1.07px] absolute bg-surface-3"></div>
                </div>
              </div>
              <div class="flex justify-start items-center gap-1">
                <div class="justify-center text-text-default/80 text-xs font-medium leading-4 tracking-tight">{{ $t('common.drafts') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div ref="fusion_body_el" class="fusion_body relative text-text-default" :style="messenger_container_height>0?`height: ${messenger_container_height}px`:''">
        <component v-if="component_ready" :key="page_type+'Component_'+(current_user_guid || 'anon')" v-bind:is="page_component" class="bg-surface-1 w-full h-full" :show-spark-splash="show_splash"
                   :set-configuration="fusion_configuration" :set-thread-id="active_thread_id" @closeSplash="show_splash=false" />
        <p v-else class="text-center my-3">
          <span v-if="component_not_ready_msg">{{ component_not_ready_msg }}</span>
        </p>
      </div>
    </template>
    <p v-else class="text-center py-5 text-text-default select-none" style="min-height: 300px" @click="handleUnauthorizedClick">{{ $FUSunauthorizedMessage }}</p>
    <options-menu v-if="!hideOptionsMenu" :trigger-show-options="show_options" @openReyConversation="openReyConversation" />
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import Navbar from '@components/Core/Navbar.vue';
import { isTrustedHostMessage } from '../../utils/embedOrigins';
import ContactDetails from '@components/Views/ContactDetails.vue';
import Messaging from '@components/Views/Messaging.vue';
import Conversations from '@components/Views/Conversations.vue';
import OptionsMenu from '@components/Settings/OptionsMenu.vue';
import ReyChatIcon from '@images/rey_chat.svg';

export default {
  props: {
    setConfiguration: {
      type: Object,
      required: true,
      validator: function (value) {
        return true;
      }
    },
    hideOptionsMenu: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  components: {
    'navbar': Navbar,
    'contact-details': ContactDetails,
    'messaging': Messaging,
    'conversations': Conversations,
    'options-menu': OptionsMenu,
  },
  created() {
    this.handleResize();
    this.setActiveTab();
    // Only the Spark body renders the ReyWelcome splash. Gate it to once-per-day-per-user
    // (server-persisted, so it holds across host apps and standalone). Non-Spark bodies leave
    // show_splash at its default (false) and never render the splash.
    if (this.page_type === 'Spark')
      this.resolveWelcomeGate();
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("message", this.handleWindowMessage);
    window.event_bus.$on('SPARK-OPEN-CONVERSATION', this.handleSparkOpenConversation);
    // created() ran handleResize before the DOM existed; take the first real chrome
    // measurement now that .fusion_body is rendered.
    this.$nextTick(() => this.measureChromeHeight());
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("message", this.handleWindowMessage);
    window.event_bus.$off('SPARK-OPEN-CONVERSATION', this.handleSparkOpenConversation);
  },
  data() {
    return {
      rey_chat_icon: ReyChatIcon,
      page_type_component: {
        ContactDetails: 'contact-details',
        InternalMessaging: 'messaging',
        Conversations: 'conversations',
        Spark: 'messaging',
      },
      window_width: 0,
      window_height: 0,
      chrome_height: 0,
      active_tab: null,
      // Starts hidden so an already-shown day never flashes the splash before hiding it.
      // resolveWelcomeGate() flips this to true only when the welcome is eligible today.
      show_splash: false,
      show_options: 0,
      nav_item_class: 'p-2 rounded-xl border flex justify-center items-center gap-1.5',
      nav_item_active_class: 'bg-item-selected border-brand-subtle',
      nav_item_inactive_class: 'border-border-separator hover:bg-brand-subtle/30 cursor-pointer',
      nav_item_label_class: 'justify-center text-text-default text-sm font-medium leading-5 tracking-[0.16px]',
      red_indicator_class: 'w-2 h-2 right-0 top-0 absolute bg-rose-400 rounded-full',
    }
  },
  computed: {
    ...mapStores(useMainStore),
    has_chat_access() {
      return this.mainStore.has_chat_access;
    },
    has_tac_access() {
      return this.mainStore.has_tac_access;
    },
    has_consumer_access() {
      return this.mainStore.has_consumer_access;
    },
    has_fusion_entitlement() {
      return this.mainStore.has_fusion_entitlement;
    },
    is_autovision_platform() {
      return this.mainStore.is_autovision_platform;
    },
    has_access() {
      let fieldData = true;
      if (this.page_type==='InternalMessaging' && !this.has_chat_access && !this.has_tac_access) {
        fieldData = false;
        this.$FUSlog('Fusion Access denied: Chat and TAC are not enabled for this user', 'warn');
      }
      
      if (this.page_type==='ContactDetails' && !this.has_consumer_access) {
        fieldData = false;
        this.$FUSlog('Fusion Access denied: Consumer is not enabled for this user', 'warn');
      }

      if (this.page_type==='Spark' && !this.has_fusion_entitlement) {
        fieldData = false;
        this.$FUSlog('Fusion Access denied: User has neither FrontRunner nor REYUSER', 'warn');
      }
      
      return fieldData;
    },
    unreads_by_thread_id() {
      return this.mainStore.unreads_by_thread_id;
    },
    channel_threads() {
      return this.mainStore.channel_threads;
    },
    user_threads() {
      return this.mainStore.user_threads;
    },
    object_threads() {
      return this.mainStore.object_threads;
    },
    internal_messaging_state: {
      get() {
        return this.mainStore.internal_messaging_state;
      },
      set(value) {
        this.mainStore.internal_messaging_state = value;
      }
    },
    component_ready() {
      switch (this.page_type) {
        case 'ContactDetails':
          return Boolean(this.contact_cdp_id || this.contact_email || this.contact_phone);
        case 'InternalMessaging':
        case 'Conversations':
          return true;
        case 'Spark':
          return Boolean(this.active_thread_id);
      }
    },
    fusion_configuration() {
      let fieldData = {};
      if(this.$FUSobjectHasFields(this.setConfiguration))
        fieldData = this.$FUSjsonCopy(this.setConfiguration);
      if (!fieldData.hasOwnProperty('FuseConfigurations'))
        fieldData['FuseConfigurations'] = {};
      fieldData['FuseConfigurations']['ActiveTab'] = this.active_tab;
      
      return fieldData;
    },
    component_not_ready_msg() {
      if(!this.component_ready) {
        switch (this.page_type) {
          case 'ContactDetails':
            return "No Contact Available."
          case 'InternalMessaging':
          case 'Spark':
            return "Error Connecting to Messenger."
        }
      }
      
      return '';
    },
    fuse_configurations() {
      return this.setConfiguration?.FuseConfigurations || {};
    },
    cif() {
      return this.fuse_configurations.cif || null;
    },
    contact_cdp_id() {
      return this.fuse_configurations.cdpId || null;
    },
    contact_email() {
      return this.fuse_configurations.email || null;
    },
    contact_phone() {
      return this.fuse_configurations.phone || null;
    },
    active_thread_id() {
      if (['Spark', 'InternalMessaging'].includes(this.page_type))
        return this.fuse_configurations.threadId || null;

      return null;
    },
    // Drives the chat component :key so the chat surface fully remounts when a different
    // user authenticates in this same SPA instance (same-device logout/login). A remount
    // resets the child's component-local thread selection, preventing the previous user's
    // thread from being re-requested (which the backend now correctly rejects with 403).
    current_user_guid() {
      return this.mainStore.user_access_info?.userId || null;
    },
    page_type() {
      return this.setConfiguration?.PageType;
    },
    page_component() {
      if(this.page_type)
        return this.page_type_component[this.page_type] || null;
      
      return null;
    },
    creating_new_thread() {
      return ['NewMessage','NewThread','NewChannel'].indexOf(this.internal_messaging_state)>=0;
    },
    show_spark_navbar_rule() {
      return this.page_type === 'Spark' && !this.show_splash;
    },
    // Reactive inputs that change the rendered chrome above .fusion_body: which sub-nav
    // (if any) renders and whether the splash container padding is applied. Watched to
    // re-measure chrome_height after the DOM settles, so we never fall back to hardcoded
    // per-page pixel constants that drift from the template.
    chrome_layout_signature() {
      return `${this.page_type}|${this.show_splash}|${this.internal_messaging_state}`;
    },
    messenger_container_height() {
      return this.window_height - this.chrome_height;
    },
    channels_unread_count() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.channel_threads) && this.$FUSobjectHasFields(this.unreads_by_thread_id)) {
        return this.channel_threads.reduce((total, thread) => {
          let threadUnreadCount = self.unreads_by_thread_id[thread.threadId] || 0;
          return total + (threadUnreadCount || 0);
        }, 0);
      }
      
      return 0;
    },
    all_unread_count() {
      // 'All' aggregates the two sources it contains: DMs + Support (TAC).
      return this.direct_messages_unread_count + this.recent_messages_unread_count + this.channels_unread_count + this.support_messages_unread_count;
    },
    direct_messages_unread_count() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.user_threads) && this.$FUSobjectHasFields(this.unreads_by_thread_id)) {
        return this.user_threads.reduce((total, thread) => {
          let threadUnreadCount = self.unreads_by_thread_id[thread.threadId] || 0;
          return total + (threadUnreadCount || 0);
        }, 0);
      }
      
      return 0;
    },
    recent_messages_unread_count() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.object_threads) && this.$FUSobjectHasFields(this.unreads_by_thread_id)) {
        return this.object_threads.filter(message => {
          return message.objectType !== 'TACChat' && !message.objectType.startsWith('SystemAI_');
        }).reduce((total, thread) => {
          let threadUnreadCount = self.unreads_by_thread_id[thread.threadId] || 0;
          return total + (threadUnreadCount || 0);
        }, 0);
      }
      
      return 0;
    },
    support_messages_unread_count() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.object_threads) && this.$FUSobjectHasFields(this.unreads_by_thread_id)) {
        return this.object_threads.filter(message => {
          return message.objectType === 'TACChat';
        }).reduce((total, thread) => {
          let threadUnreadCount = self.unreads_by_thread_id[thread.threadId] || 0;
          return total + (threadUnreadCount || 0);
        }, 0);
      }
      
      return 0;
    },
    spark_messages_unread_count() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.object_threads) && this.$FUSobjectHasFields(this.unreads_by_thread_id)) {
        return this.object_threads.filter(message => {
          return message.objectType.startsWith('SystemAI_');
        }).reduce((total, thread) => {
          let threadUnreadCount = self.unreads_by_thread_id[thread.threadId] || 0;
          return total + (threadUnreadCount || 0);
        }, 0);
      }
      
      return 0;
    },
  },
  methods: {
    // Wrapper: resolve the once-per-day ReyWelcome gate for the current user and reveal the
    // splash only if it has not been shown today. Marks it shown at the moment we decide to
    // display it, so navigating away or refreshing without closing it does not re-show it later
    // today. Fail-open — if the gate cannot be resolved (e.g. a settings outage) we still show
    // the welcome, matching the historical always-show behavior, and do not mark it shown.
    //
    // THE MORNING BRIEF DOES NOT SUPPRESS THIS. An earlier version stood the splash down when the
    // owner had an unread brief, reasoning that the brief's own header greets them with the same
    // "Welcome back" and being welcomed twice is worse than once. That was the wrong trade: the
    // welcome screen is the way into the day, and the brief belongs BEHIND it rather than instead of
    // it. MessengerWindow.openTodaysBriefIfUnread still does the landing, so dismissing the welcome
    // leaves the owner on the brief — the first thread they are on for the day.
    resolveWelcomeGate() {
      const self = this;
      this.mainStore.EnsureWelcomeGate().then(eligible => {
        if (eligible) {
          self.show_splash = true;
          self.markWelcomeShown();
        }
      }, () => {
        self.show_splash = true;
      });
    },
    // Wrapper: persist that the welcome was shown today so it will not reappear until tomorrow.
    markWelcomeShown() {
      this.mainStore.MarkWelcomeShown();
    },
    handleUnauthorizedClick() {
      window.event_bus.$emit('UNAUTHORIZED-CLICK');
    },
    // A scheduled result opened from this body's own Schedules inbox (OptionsMenu). The Spark body
    // opens it in place. Any other body — the WingCommander extension's Internal Messages or Client
    // Record blade, each its own page with no Rey panel in it — asks the host to open its Rey blade
    // on the conversation, the way SUPPORT-CHAT-STARTED asks it to open Internal Messages.
    openReyConversation(threadId) {
      if (!threadId) return;
      if (this.page_type === 'Spark')
        this.requestSparkConversationOpen(threadId);
      else
        this.$FUSpostFusionMessage('OPEN-REY-CONVERSATION', { threadId });
    },
    // Wrapper: ask the Spark panel to open one Rey conversation.
    requestSparkConversationOpen(threadId) {
      this.mainStore.RequestSparkConversationOpen(threadId);
    },
    // Someone has asked the Spark panel for a particular conversation, so the welcome splash has done
    // its job: it replaces the chat window while it is up, and leaving it there would keep the
    // conversation they asked for out of sight. The splash was marked shown when it went up, so taking
    // it down costs the day nothing. Spark only — the other bodies have no splash.
    handleSparkOpenConversation() {
      if (this.page_type === 'Spark' && this.show_splash)
        this.show_splash = false;
    },
    handleWindowMessage(ev) {
      let receivedMessage = ev?.data || null;
      let messageSource = receivedMessage?.source || null;
      let messageData = receivedMessage?.data || null;
      let messageType = receivedMessage?.type || null;
      // FUS-474: only a host above us, from an allowed origin. CONTEXT_INFO_BROADCAST becomes the thread
      // context sent with Rey messages, and this listener had no filter at all.
      if (!isTrustedHostMessage(ev))
        return;
      switch (messageType) {
        case 'SHOW-OPTIONS':
          this.show_options = Date.now();
          break;
        case 'CONTEXT_INFO_BROADCAST':
          this.mainStore.thread_context = messageData;
          break;
        default:
          break;
      }
    },
    setActiveTab(activeTab=null) {
      if(this.page_type === 'InternalMessaging')
        this.internal_messaging_state = null;
      if(activeTab===null) {
        switch (this.page_type) {
          case 'InternalMessaging':
            if(this.has_chat_access)
              this.active_tab = 'All';
            else if(this.has_tac_access)
              this.active_tab = 'Support';
            else
              this.active_tab = null;
            break;
          case 'Spark':
            this.active_tab = 'Chat';
            break;
        }
      }
      else
        this.active_tab = activeTab;
    },
    handleResize() {
      this.window_width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      this.window_height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      // Width changes (and FusionMain's synthetic resize) can reflow the chrome; re-measure
      // once the layout settles.
      this.$nextTick(() => this.measureChromeHeight());
    },
    // Measure the actual chrome above .fusion_body instead of summing hardcoded per-page
    // pixel constants. The body's document-relative top is exactly the height of everything
    // above it (navbar wrapper + sub-nav + container top padding + any ancestor offset);
    // adding the container's bottom padding leaves .fusion_body filling the viewport exactly,
    // so the document no longer overflows. getBoundingClientRect().top + window.scrollY keeps
    // this scroll-independent.
    measureChromeHeight() {
      const bodyEl = this.$refs.fusion_body_el;
      const containerEl = this.$refs.fusion_body_container;
      if (!bodyEl || !containerEl) {
        this.chrome_height = 0;
        return;
      }
      const bodyTop = bodyEl.getBoundingClientRect().top + window.scrollY;
      const bottomPad = parseFloat(window.getComputedStyle(containerEl).paddingBottom) || 0;
      this.chrome_height = Math.max(0, Math.round(bodyTop + bottomPad));
    },
  },
  watch: {
    chrome_layout_signature() {
      this.$nextTick(() => this.measureChromeHeight());
    },
  },
}
</script>
